// @ts-check

const SELECTORS = {
  trigger: "[data-behaviour~=generate-schedule-dates]",
  container: "[data-behaviour~=nested-insert-container]",
  loadError: "[data-behaviour~=generate-schedule-dates-error]",
};

/**
 * PREFETCH_DWELL_MS is how long a pointer rests on the trigger before the
 * chunks are fetched, so a pointer crossing the link on its way elsewhere
 * fetches nothing.
 */
const PREFETCH_DWELL_MS = 150;

/**
 * BUILD_TIMEOUT_MS bounds the wait for the app: past it the click reports the
 * still-loading state instead of holding the trigger busy indefinitely.
 */
const BUILD_TIMEOUT_MS = 15000;

/**
 * @typedef {Object} GeneratorApp
 * @property {Object} store drives the modal: `configure({existingSlots})` then `open()`
 * @property {typeof import("./form-rows")} formRows reads and writes the form's date rows
 */

/**
 * @typedef {Object} GeneratorCallbacks
 * @property {(slots: Array) => Boolean} onConfirmSlots receives the confirmed slots; false means no row was appended
 * @property {() => void} onClose the dialog closed, however it was dismissed
 */

/**
 * Wires the "Generate dates" trigger to the generator app. The click listener is
 * document-delegated, so a trigger injected after page load still works; the app
 * is built on the first click. On a page that has a trigger when this runs, a
 * dwelt hover or a focus fetches the app's chunks ahead of that click — pages
 * without one carry only the click listener.
 *
 * A failed chunk fetch is permanent (the browser's module map replays the
 * rejection) and reports that the page needs reloading; a failed app build is
 * retried on the next click; a build that outlives the timeout reports the
 * still-loading state while the build continues.
 *
 * @param {Object} [options]
 * @param {(callbacks: GeneratorCallbacks) => Promise<GeneratorApp>} [options.loadApp]
 * @param {() => Promise<unknown>} [options.warmChunks]
 * @returns {() => void} uninstalls the listeners
 */
export function wireScheduleDateGenerator({ loadApp = mountApp, warmChunks = warmAppChunks } = {}) {
  let whenReady = null;
  let warmed = false;
  let dwellTimer = null;
  /** @type {{container: Element, formRows: typeof import("./form-rows")}|null} */
  let active = null;

  function handleConfirmSlots(slots) {
    if (!active || !active.container.isConnected) {
      return false;
    }

    return active.formRows.insertRows(active.container, slots).count > 0;
  }

  function handleClose() {
    active = null;
  }

  /**
   * @returns {Promise<GeneratorApp>}
   */
  function build() {
    warmed = true;
    whenReady = loadApp({ onConfirmSlots: handleConfirmSlots, onClose: handleClose });

    // A build started before any click has nothing awaiting it yet. The click
    // that awaits the promise is what reports a rejection.
    whenReady.catch(() => {});

    return whenReady;
  }

  async function handleClick(event) {
    const trigger = closestTrigger(event.target);

    if (!trigger) {
      return;
    }

    event.preventDefault();

    const container = trigger.closest(SELECTORS.container);

    if (!container) {
      return;
    }

    hideLoadError(container);
    trigger.setAttribute("aria-busy", "true");

    /** @type {GeneratorApp} */
    let app;

    try {
      app = await withTimeout(whenReady || build());
    } catch (error) {
      // An errored chunk fetch replays from the module map, and a timed-out
      // build is still pending — only an app build failure earns a fresh start.
      if (!isPermanentFailure(error) && !isStillLoading(error)) {
        whenReady = null;
      }

      reportFailedLoad(container, error);
      return;
    } finally {
      trigger.removeAttribute("aria-busy");
    }

    // The container may have been detached while the build was in flight.
    if (!container.isConnected) {
      return;
    }

    // The rows are read after the await, so a row removed while the app was
    // still loading is not treated as on the list.
    active = { container, formRows: app.formRows };
    app.store.configure({ existingSlots: app.formRows.readExistingSlots(container) });
    app.store.open();
  }

  function warm() {
    if (!warmed) {
      warmed = true;
      warmChunks().catch(() => {});
    }
  }

  function handlePointerOver(event) {
    if (!warmed && !dwellTimer && closestTrigger(event.target)) {
      dwellTimer = setTimeout(() => {
        dwellTimer = null;
        warm();
      }, PREFETCH_DWELL_MS);
    }
  }

  function handlePointerOut(event) {
    if (dwellTimer && closestTrigger(event.target)) {
      clearTimeout(dwellTimer);
      dwellTimer = null;
    }
  }

  function handleFocusIn(event) {
    if (closestTrigger(event.target)) {
      warm();
    }
  }

  document.addEventListener("click", handleClick);

  // The warm listeners ride every pointer move, so they are only worth having
  // on a page that renders a trigger. One injected later still works — its
  // click builds the app, with the trigger's busy state as feedback.
  if (document.querySelector(SELECTORS.trigger)) {
    document.addEventListener("pointerover", handlePointerOver);
    document.addEventListener("pointerout", handlePointerOut);
    document.addEventListener("focusin", handleFocusIn);
  }

  return () => {
    document.removeEventListener("click", handleClick);
    document.removeEventListener("pointerover", handlePointerOver);
    document.removeEventListener("pointerout", handlePointerOut);
    document.removeEventListener("focusin", handleFocusIn);

    if (dwellTimer) {
      clearTimeout(dwellTimer);
      dwellTimer = null;
    }
  };
}

/**
 * @param {EventTarget|null} target
 * @returns {Element|null}
 */
function closestTrigger(target) {
  return target instanceof Element ? target.closest(SELECTORS.trigger) : null;
}

/**
 * @param {unknown} error
 * @returns {Boolean}
 */
function isPermanentFailure(error) {
  return Boolean(error && error.permanentLoadFailure);
}

/**
 * @param {unknown} error
 * @returns {Boolean}
 */
function isStillLoading(error) {
  return Boolean(error && error.stillLoading);
}

/**
 * Resolves with `promise`'s value, or rejects after `BUILD_TIMEOUT_MS` with a
 * `stillLoading` error. The promise itself is untouched: a build that settles
 * after a timeout can still serve a later click.
 *
 * @template T
 * @param {Promise<T>} promise
 * @returns {Promise<T>}
 */
function withTimeout(promise) {
  return new Promise((resolve, reject) => {
    const timer = setTimeout(() => {
      const error = new Error(`The schedule date generator did not load within ${BUILD_TIMEOUT_MS}ms`);

      error.stillLoading = true;
      reject(error);
    }, BUILD_TIMEOUT_MS);

    const settle = (handler) => (value) => {
      clearTimeout(timer);
      handler(value);
    };

    promise.then(settle(resolve), settle(reject));
  });
}

/**
 * Shows the form's message for the failure: the still-loading one for a
 * timed-out build, otherwise the "could not be loaded" one, which asks the
 * editor to reload — the recovery for a chunk whose hashed filename a deploy
 * rotated away, since the module map replays the failed fetch without
 * refetching.
 *
 * @param {Element} container
 * @param {unknown} error
 */
function reportFailedLoad(container, error) {
  console.error("The schedule date generator failed to load; dates must be added one at a time", error);
  showLoadError(container, isStillLoading(error) ? "pending" : "unavailable");
}

/**
 * Renders the message into the live region at reveal time — several screen
 * readers do not announce an unhidden node, only inserted or changed content —
 * and points the trigger at it. The description is attached only while the
 * message is true, because `aria-describedby` includes a directly referenced
 * node's text even when hidden.
 *
 * @param {Element} container
 * @param {"unavailable"|"pending"} kind
 */
function showLoadError(container, kind) {
  const message = loadErrorElement(container);

  if (!message) {
    return;
  }

  message.textContent = kind === "pending" ? message.dataset.pendingMessage : message.dataset.unavailableMessage;
  message.hidden = false;

  if (message.id) {
    container.querySelector(SELECTORS.trigger)?.setAttribute("aria-describedby", message.id);
  }
}

/**
 * @param {Element} container
 */
function hideLoadError(container) {
  const message = loadErrorElement(container);

  if (!message) {
    return;
  }

  message.textContent = "";
  message.hidden = true;
  container.querySelector(SELECTORS.trigger)?.removeAttribute("aria-describedby");
}

/**
 * @param {Element} container
 * @returns {HTMLElement|null}
 */
function loadErrorElement(container) {
  const message = container.querySelector(SELECTORS.loadError);

  return message instanceof HTMLElement ? message : null;
}

/**
 * Fetches the generator's chunks without instantiating anything, so a hover or
 * focus pays the network cost ahead of the click without paying the app build.
 *
 * @returns {Promise<unknown>}
 */
function warmAppChunks() {
  return Promise.all([import("#/admin/schedule_date_generator"), import("./form-rows")]);
}

/**
 * Loads the app and the row IO it writes through, so neither ships in the eager
 * admin bundle that installs this wiring. An import failure is marked
 * `permanentLoadFailure`: the module map replays it, so a retry cannot recover.
 *
 * @param {GeneratorCallbacks} callbacks
 * @returns {Promise<GeneratorApp>}
 */
async function mountApp(callbacks) {
  let modules;

  try {
    modules = await Promise.all([import("#/admin/schedule_date_generator"), import("./form-rows")]);
  } catch (error) {
    if (error instanceof Error) {
      error.permanentLoadFailure = true;
    }

    throw error;
  }

  const [{ createAppInstance }, formRows] = modules;
  const { app, store } = await createAppInstance(callbacks);
  const mount = document.createElement("div");

  document.body.appendChild(mount);

  try {
    app.mount(mount);
  } catch (error) {
    mount.remove();
    throw error;
  }

  return { store, formRows };
}
